/**
 * FeedbackComponents.tsx
 * Feedback & Overlays components category page - Modal, Alert, Toast, Tooltip, Popover
 * 
 * Showcases all feedback and overlay-related HeroUI components with organized layout
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody, CardFooter } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { MessageCircle, Square, AlertTriangle, Bell, HelpCircle, MessageSquare, CircleDot, Loader, BarChart3, Layers } from 'lucide-react';

export const FeedbackComponents: React.FC = () => {
  const navigate = useNavigate();

  const feedbackComponents = [
    {
      name: 'Modal',
      description: 'Overlay dialog for user interactions and confirmations',
      status: 'available',
      heroUIBase: 'Modal',
      route: '/playground/components/feedback/modal',
      icon: Square,
      preview: (
        <div className="text-xs space-y-2">
          <div className="relative bg-surface border border-border rounded p-2">
            <div className="absolute inset-0 bg-black/20 rounded"></div>
            <div className="relative bg-background border border-border rounded p-2 m-1">
              <div className="flex justify-between items-center mb-1">
                <div className="font-medium">Dialog Title</div>
                <div className="w-2 h-2 bg-text-tertiary rounded"></div>
              </div>
              <div className="text-text-secondary">Modal content</div>
            </div>
          </div>
          <div className="text-text-tertiary text-center">Interactive Overlays</div>
        </div>
      )
    },
    {
      name: 'Alert',
      description: 'Static messages for informing users about important information',
      status: 'available',
      heroUIBase: 'Alert',
      route: '/playground/components/feedback/alert',
      icon: AlertTriangle,
      preview: (
        <div className="text-xs space-y-1">
          <div className="flex items-center gap-2 p-2 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded">
            <div className="w-3 h-3 bg-warning-500 rounded-full"></div>
            <div className="font-medium text-warning-700 dark:text-warning-300">Warning message</div>
          </div>
          <div className="text-text-tertiary text-center">Status Messages</div>
        </div>
      )
    },
    {
      name: 'Toast',
      description: 'Temporary notifications that appear and disappear automatically',
      status: 'available',
      heroUIBase: 'Toast',
      route: '/playground/components/feedback/toast',
      icon: Bell,
      preview: (
        <div className="text-xs space-y-1">
          <div className="flex items-center gap-2 p-2 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded shadow-sm">
            <div className="w-3 h-3 bg-success-500 rounded-full"></div>
            <div className="font-medium text-success-700 dark:text-success-300">Success!</div>
            <div className="w-2 h-2 bg-text-tertiary rounded ml-auto"></div>
          </div>
          <div className="text-text-tertiary text-center">Notifications</div>
        </div>
      )
    },
    {
      name: 'Tooltip',
      description: 'Contextual help text that appears on hover or focus',
      status: 'available',
      heroUIBase: 'Tooltip',
      route: '/playground/components/feedback/tooltip',
      icon: HelpCircle,
      preview: (
        <div className="text-xs space-y-1 relative">
          <div className="flex justify-center">
            <div className="relative inline-flex items-center">
              <div className="w-4 h-4 bg-primary rounded border-2 border-primary-200"></div>
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-black text-white px-2 py-1 rounded text-xs whitespace-nowrap">
                Helpful tip
              </div>
            </div>
          </div>
          <div className="text-text-tertiary text-center mt-4">Contextual Help</div>
        </div>
      )
    },
    {
      name: 'Popover',
      description: 'Rich content overlay triggered by user interaction',
      status: 'available',
      heroUIBase: 'Popover',
      route: '/playground/components/feedback/popover',
      icon: MessageSquare,
      preview: (
        <div className="text-xs space-y-1 relative">
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-6 h-4 bg-primary rounded"></div>
              <div className="absolute -top-8 -left-4 bg-background border border-border rounded p-2 shadow-lg">
                <div className="font-medium mb-1">Rich Content</div>
                <div className="text-text-secondary">More details</div>
              </div>
            </div>
          </div>
          <div className="text-text-tertiary text-center mt-6">Rich Overlays</div>
        </div>
      )
    },
    {
      name: 'Circular Progress',
      description: 'Circular loading indicators with customizable appearance and value display',
      status: 'available',
      heroUIBase: 'CircularProgress',
      route: '/playground/components/feedback/circular-progress',
      icon: CircleDot,
      preview: (
        <div className="text-xs space-y-2">
          <div className="flex items-center justify-center">
            <div className="relative w-8 h-8">
              <div className="absolute inset-0 rounded-full border-2 border-border"></div>
              <div className="absolute inset-0 rounded-full border-2 border-primary border-t-transparent animate-spin"></div>
            </div>
          </div>
          <div className="text-text-tertiary text-center">Loading Progress</div>
        </div>
      )
    },
    {
      name: 'Spinner',
      description: 'Simple animated loading indicators for various loading states',
      status: 'available',
      heroUIBase: 'Spinner',
      route: '/playground/components/feedback/spinner',
      icon: Loader,
      preview: (
        <div className="text-xs space-y-2">
          <div className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <div className="w-1 h-1 bg-primary rounded-full animate-pulse"></div>
            <div className="w-1 h-1 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-1 h-1 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
          </div>
          <div className="text-text-tertiary text-center">Loading States</div>
        </div>
      )
    },
    {
      name: 'Progress',
      description: 'Linear progress indicators for loading states and progress tracking',
      status: 'available',
      heroUIBase: 'Progress',
      route: '/playground/components/feedback/progress',
      icon: BarChart3,
      preview: (
        <div className="text-xs space-y-2">
          <div className="w-full bg-default-200 rounded-full h-2">
            <div className="bg-primary-500 h-2 rounded-full" style={{ width: '60%' }}></div>
          </div>
          <div className="text-text-tertiary text-center">Loading Progress</div>
        </div>
      )
    },
    {
      name: 'Skeleton',
      description: 'Loading placeholders that maintain layout structure',
      status: 'available',
      heroUIBase: 'Skeleton',
      route: '/playground/components/feedback/skeleton',
      icon: Layers,
      preview: (
        <div className="text-xs space-y-1">
          <div className="w-full h-2 bg-default-200 rounded animate-pulse"></div>
          <div className="w-3/4 h-2 bg-default-200 rounded animate-pulse"></div>
          <div className="w-1/2 h-2 bg-default-200 rounded animate-pulse"></div>
          <div className="text-text-tertiary text-center mt-2">Content Loading</div>
        </div>
      )
    }
  ];

  const handleComponentClick = (route: string) => {
    navigate(route);
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-6">
        
        {/* Category Header */}
        <div className="text-center px-4">
          <div className="flex items-center justify-center gap-3 mb-4">
            <MessageCircle className="w-6 h-6 md:w-8 md:h-8 text-primary" />
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary">Feedback & Overlays</h1>
          </div>
          <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
            Components for user feedback, notifications, and contextual information.
            Provide clear communication and enhanced user experience with modals, alerts, and overlays.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-4">
            <Badge color="success" variant="flat">
              {feedbackComponents.filter(c => c.status === 'available').length} available
            </Badge>
            <Badge color="warning" variant="flat">
              {feedbackComponents.filter(c => c.status === 'not-started').length} planned
            </Badge>
            <Chip size="sm" color="primary" variant="dot">HeroUI Based</Chip>
          </div>
        </div>

        {/* Components Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 px-4 md:px-0">
          {feedbackComponents.map((component) => {
            const IconComponent = component.icon;
            const isAvailable = component.status === 'available';
            
            return (
              <Card
                key={component.name}
                isPressable={isAvailable}
                {...(isAvailable && { onPress: () => handleComponentClick(component.route) })}
                className={`group transition-all duration-200 ${
                  isAvailable 
                    ? 'hover:shadow-lg cursor-pointer' 
                    : 'opacity-75 cursor-not-allowed'
                }`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between w-full gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <IconComponent className="w-5 h-5 text-primary flex-shrink-0" />
                      <h3 className="font-semibold text-primary text-base md:text-lg truncate">{component.name}</h3>
                    </div>
                    <Chip 
                      color={
                        component.status === 'available' ? 'success' : 
                        component.status === 'not-started' ? 'default' : 'warning'
                      }
                      size="sm"
                      variant="flat"
                      className="flex-shrink-0"
                    >
                      {component.status === 'available' ? 'available' : 
                       component.status === 'not-started' ? 'planned' : component.status}
                    </Chip>
                  </div>
                </CardHeader>
                
                <CardBody className="space-y-4">
                  <p className="text-text-secondary">{component.description}</p>
                  
                  {/* Component Preview */}
                  <div className="p-4 bg-background rounded-lg border border-border">
                    {component.preview}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Chip size="sm" variant="bordered" color="primary">
                      {component.heroUIBase}
                    </Chip>
                  </div>
                </CardBody>
                
                <CardFooter>
                  <div className={`w-full text-center py-1 px-3 rounded-md border transition-colors ${
                    isAvailable 
                      ? 'border-primary text-primary group-hover:bg-primary group-hover:text-primary-foreground' 
                      : 'border-default-200 text-default-400'
                  }`}>
                    {isAvailable ? 'View Examples & Documentation →' : 'Coming Soon'}
                  </div>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Category Info */}
        <Card className="bg-surface">
          <CardBody>
            <div className="text-center space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">About Feedback & Overlay Components</h3>
              <p className="text-text-secondary max-w-3xl mx-auto">
                Feedback and overlay components help communicate information to users and provide contextual interactions. 
                These components enhance user experience by providing clear feedback, notifications, and focused interactions 
                while maintaining accessibility and consistent theming.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Chip size="sm" variant="flat" color="primary">User Communication</Chip>
                <Chip size="sm" variant="flat" color="secondary">Focus Management</Chip>
                <Chip size="sm" variant="flat" color="success">Accessibility Focused</Chip>
                <Chip size="sm" variant="flat" color="warning">Theme Integrated</Chip>
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};